import { useCallback, useEffect, useRef, useState } from 'react'

const STORAGE_KEY = 'smartSearchesRemaining'
const EXCEEDED_KEY = 'smartSearchesExceeded'
const RESET_DATE_KEY = 'smartSearchesResetDate'
const FREE_LIMIT = 10

function getTodayDateString() {
	return new Date().toLocaleDateString('en-us')
}

function getNextMidnight() {
	const tomorrow = new Date()
	tomorrow.setDate(tomorrow.getDate() + 1)
	tomorrow.setHours(0, 0, 0, 0)
	return tomorrow.getTime()
}

export function formatResetTime(timestamp: number): string {
	const now = Date.now()
	const hoursLeft = Math.ceil((timestamp - now) / (1000 * 60 * 60))

	if (hoursLeft <= 1) return 'in less than an hour'
	if (hoursLeft < 12) return `in ${hoursLeft} hours`
	return 'at midnight'
}

export function useFreeSmartSearchLimit(_query: string, isActive: boolean) {
	const [remaining, setRemainingState] = useState(FREE_LIMIT)
	const [exceeded, setExceededState] = useState(false)
	const [isHydrated, setIsHydrated] = useState(false)
	const [resetTime, setResetTime] = useState<number | null>(null)
	const decrementedQueries = useRef<Set<string>>(new Set())

	// Hydrate from localStorage - check for daily reset
	useEffect(() => {
		const today = getTodayDateString()
		const storedDate = localStorage.getItem(RESET_DATE_KEY)

		if (storedDate !== today) {
			// New day - reset everything
			localStorage.setItem(STORAGE_KEY, String(FREE_LIMIT))
			localStorage.setItem(RESET_DATE_KEY, today)
			localStorage.removeItem(EXCEEDED_KEY)
			setRemainingState(FREE_LIMIT)
			setExceededState(false)
			decrementedQueries.current.clear()
		} else {
			// Same day - load existing values
			const stored = localStorage.getItem(STORAGE_KEY)
			if (stored !== null) {
				const parsed = parseInt(stored, 10)
				if (!isNaN(parsed)) setRemainingState(parsed)
			}
			const exceededStored = localStorage.getItem(EXCEEDED_KEY)
			if (exceededStored === 'true') setExceededState(true)
		}

		setResetTime(getNextMidnight())
		setIsHydrated(true)
	}, [])

	const setRemaining = useCallback((value: number) => {
		setRemainingState(value)
		localStorage.setItem(STORAGE_KEY, String(value))
	}, [])

	const setExceeded = useCallback((value: boolean) => {
		setExceededState(value)
		localStorage.setItem(EXCEEDED_KEY, String(value))
	}, [])

	// Call this when results return successfully
	const decrementSearch = useCallback(
		(completedQuery: string) => {
			if (!isActive) return
			if (completedQuery.length < 3) return
			if (exceeded) return
			if (decrementedQueries.current.has(completedQuery)) return

			decrementedQueries.current.add(completedQuery)

			if (remaining > 0) {
				setRemaining(remaining - 1)
			} else {
				setExceeded(true)
			}
		},
		[isActive, remaining, exceeded, setRemaining, setExceeded],
	)

	return {
		remaining: exceeded ? ('done' as const) : remaining,
		hasSearchesLeft: !exceeded,
		isExceeded: isHydrated && exceeded,
		isHydrated,
		decrementSearch,
		resetTime,
	}
}
